import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ListResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalyticalDatasetResultRow } from './types';

// How long a fetched page counts as fresh. The grid shows a loading overlay on
// any refetch, so a page prefetched before the user opens the grid has to still
// be fresh when it mounts, or the grid would reload it straight away. A finished
// run invalidates these entries by prefix (see `useAnalysisProfileQuery`), so
// this only bounds staleness from changes made outside this page.
export const DATASET_RESULTS_STALE_TIME_MS = 60_000;

interface AnalyticalDatasetResultsQueryArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** Serialized grid params (`page=…&pageSize=…&sort=…&filter=…`) from the shared DataGrid. */
  searchParams?: string;
}

/**
 * Query definition for one page of per-patient results for an analytical
 * dataset's latest PROCESSED run. Shared by the grid's hook and the prefetch so
 * both read and write the same cache entry.
 */
export const analyticalDatasetResultsQueryOptions = ({
  projectId,
  analyticalDatasetProfileId,
  searchParams,
}: AnalyticalDatasetResultsQueryArgs) => {
  // The shared DataGrid serializes its state with a leading '?'; re-parse to a
  // bare string so the URL can't end up as `results??page=…`, which the backend
  // reads as a '?page' key and serves page 1 forever.
  const normalizedSearchParams = searchParams ? new URLSearchParams(searchParams).toString() : '';

  return queryOptions({
    queryKey: analysisKeys.datasetResults(analyticalDatasetProfileId, normalizedSearchParams),
    queryFn: () =>
      authFetch<ListResponse<AnalyticalDatasetResultRow>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}` +
          `/results${normalizedSearchParams ? `?${normalizedSearchParams}` : ''}`
      ),
    enabled: projectId.length > 0 && analyticalDatasetProfileId.length > 0,
    retry: false,
    staleTime: DATASET_RESULTS_STALE_TIME_MS,
  });
};

/** One page of per-patient results for an analytical dataset's latest PROCESSED run. */
export const useAnalyticalDatasetResultsQuery = (args: AnalyticalDatasetResultsQueryArgs) =>
  useQuery({
    ...analyticalDatasetResultsQueryOptions(args),
    // Keep the previous page's rows while the next page loads so the grid
    // doesn't collapse to its loading skeleton on every pagination click.
    placeholderData: keepPreviousData,
  });
